<template>
  <div ref="graphCounter" style="width:100%; height:100%; background-color: var(--color-g1);"></div>
</template>

<script setup lang="ts" >
import Plotly from 'plotly.js-dist-min'
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t, d, locale, getDateTimeFormat } = useI18n();

function isMobileOrTouchDevice() {
  const isMobileUserAgent = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  if (isMobileUserAgent) {
    return true;
  }
  // If the user agent is not mobile, consider it touch only if the primary pointer is coarse.
  // Pointer coarse: The primary input mechanism includes a pointing device of limited accuracy, 
  // such as a finger on a touchscreen.
  const isPrimaryPointerCoarse = window.matchMedia('(pointer: coarse)').matches;
  
  // This control excludes devices with touchpad 
  // (configured as isPrimaryPointerCoarse=false and navigator.maxTouchPoints > 0)
  // but might still include 2-in-1 PCs in tablet mode.
  if (isPrimaryPointerCoarse && navigator.maxTouchPoints > 0) {
    return true;
  }
  return false;
}

const graphCounter = ref<HTMLDivElement>();

const props = defineProps<{
  graphData: Array<{ timestamp: string, value: number }>;
}>();

const drawGraph = async () => { 
  // Prepare data
  let series = [
    convertToPlotlyFormat( props.graphData )
  ];

  // Clear graph if there is no data
  if ( series[0].x.length == 0 ) {
    const isPlotyInstalledOn = ( html: any ) => html && html.data && html.layout;
    isPlotyInstalledOn( graphCounter.value ) && Plotly.deleteTraces( graphCounter.value, 0 );
    return;
  }

  // Configure and draw
  const g1 = getComputedStyle(document.documentElement).getPropertyValue("--color-g1");
  let layout = {
    plot_bgcolor: g1,
    paper_bgcolor: g1,
    title: '',
    font: {
      size: 8,
      family: "Roboto"
    },
    showlegend: false,
    yaxis: {
      autorange: true
    },
    xaxis: {
      tickformat: getDateTimeFormat(locale.value).dateHour,
    },
    height: 160,
    margin: {
      l: 40,
      t: 40,
      b: 40,
      r: 40,
      pad: 0
    },
    
  };
  Plotly.newPlot( graphCounter.value, series, layout, {
    displayModeBar: false,
    ...(isMobileOrTouchDevice() && {
      staticPlot: true,
    }),
  } );
}

const convertToPlotlyFormat = ( samples: Array<{timestamp: string, value: number}> ): { x: Date[], y: number[], type: string, mode: string } => {
  if ( samples.length == 0 )
  {
    return { x: [], y: [], type: 'scatter', mode: 'lines' }
  }
  const data = {
    x: new Array<Date>( samples.length ),
    y: new Array<number>( samples.length ),
    type: 'scatter',
    mode: 'lines'
  };
  for( let i = 0;i < samples.length; i++ )
  {
    const sample = samples[i];
    data.x[i] = new Date( sample.timestamp);
    data.y[i] = sample.value;
  }
  return data;
}

watch(() => props.graphData, async (newVal: Array<{ timestamp: string, value: number }>) => {
  await drawGraph();
});
</script>

<style>

.theme--light.v-datatable thead th.column.sortable.active, .theme--light.v-datatable thead th.column.sortable.active .v-icon, .theme--light.v-datatable thead th.column.sortable:hover
{
  margin-left:10px;
}

</style>

